import { Selector } from '@ngxs/store';
import { CapaciteState, CapaciteStateModel } from './capacite.state';
import { get } from 'lodash';
import { EtapeDettesDataModel } from './modeles/etape-dettes-data.model';
import { EtapeHypothequeDataModel } from './modeles/etape-hypotheque-data.model';
import { EtapePaiementDataModel } from './modeles/etape-paiement-data.model';
import { EtapeDepensesDataModel } from './modeles/etape-depenses-data.model';
import { EtapeDepensesPaiementDataModel } from './modeles/etape-depenses-paiement-data.model';
import { ResultatDataModel } from './modeles/resultat-data.model';
import { CapaciteHelper } from './helpers/capacite-helper/capacite-helper';
import { FrequenceEnum, FREQUENCES } from '../../shared/constants/frequence.config';
import { TypeCapaciteEnum } from './constants/type-capacite.enum';
import { CapaciteEditStepEnum } from './constants/capacite-edit-step.enum';
import { StatusCapaciteEnum } from './constants/status-capacite.enum';
import { CapaciteModel } from './modeles/capacite.model';
import { TypeProjet } from '../../shared/constants/type-projet.enum';

const getFromPath = function(state: CapaciteStateModel, path) {
  if (get(state.pendingChanges, path) === 0 || get(state.capacite, path) === 0) {
    return 0;
  }

  return get(state.pendingChanges, path) || get(state.capacite, path) || null;
};

export class CapaciteSelectors {
  @Selector([CapaciteState])
  static donneesEtapeRevenus(state: CapaciteStateModel) {
    return {
      revenuAnnuelBrut: getFromPath(state, 'bilanSommaire.revenuAnnuelBrut'),
      revenuNet: getFromPath(state, 'bilanSommaire.revenuNet'),
      codeFrequenceRevenuNet: getFromPath(state, 'bilanSommaire.codeFrequenceRevenuNet')
    };
  }

  @Selector([CapaciteState])
  static typeCapacite(state: CapaciteStateModel) {
    return state.capacite.typeCapacite === null ? TypeProjet.PremierAcheteur : state.capacite.typeCapacite;
  }

  @Selector([CapaciteState])
  static currentStep(state: CapaciteStateModel) {
    return state.currentStep;
  }

  @Selector([CapaciteState])
  static isCapaciteParDepenses(state: CapaciteStateModel) {
    return !this.isCapacitePaiementMaximal(state);
  }

  @Selector([CapaciteState])
  static isCapacitePaiementMaximal(state: CapaciteStateModel) {
    return state.capacite.indicateurLimiteMontantVersement === 'O';
  }

  @Selector([CapaciteState])
  static isCapacityCompleted(state: CapaciteStateModel) {
    return state.completed;
  }

  @Selector([CapaciteState])
  static isCapacityCompletedAndLoaded(state: CapaciteStateModel) {
    return { isCapaciteLoaded: state.loaded, isCapaciteCompleted: state.completed };
  }

  @Selector([CapaciteState])
  static donneesQuestionTypePropriete(state: CapaciteStateModel) {
    return state.capacite.typePropriete;
  }

  @Selector([CapaciteState])
  static donneesQuestionProjet(state: CapaciteStateModel) {
    return getFromPath(state, 'typeCapacite');
  }

  @Selector([CapaciteState])
  static donneesEtapeMiseFonds(state: CapaciteStateModel): { miseDeFonds: number } {
    return {
      miseDeFonds: getFromPath(state, 'miseDeFonds')
    };
  }

  @Selector([CapaciteState])
  static donneesEtapeDette(state: CapaciteStateModel): EtapeDettesDataModel {
    return {
      indicateurCarteCredit: getFromPath(state, 'indicateurCarteCredit'),
      bilanSommaire: {
        carteCredit: getFromPath(state, 'bilanSommaire.carteCredit'),
        codeFrequenceCarteCredit: getFromPath(state, 'bilanSommaire.codeFrequenceCarteCredit'),
        margeCredit: getFromPath(state, 'bilanSommaire.margeCredit'),
        codeFrequenceMargeCredit: getFromPath(state, 'bilanSommaire.codeFrequenceMargeCredit'),
        pretAuto: getFromPath(state, 'bilanSommaire.pretAuto'),
        codeFrequencePretAuto: getFromPath(state, 'bilanSommaire.codeFrequencePretAuto'),
        pretPersonnel: getFromPath(state, 'bilanSommaire.pretPersonnel'),
        codeFrequencePretPersonnel: getFromPath(state, 'bilanSommaire.codeFrequencePretPersonnel'),
        autrePret: getFromPath(state, 'bilanSommaire.autrePret'),
        codeFrequenceAutrePret: getFromPath(state, 'bilanSommaire.codeFrequenceAutrePret')
      }
    };
  }

  @Selector([CapaciteState])
  static donneesEtapeBudget(state: CapaciteStateModel) {
    return {
      indicateurLimiteMontantVersement: getFromPath(state, 'indicateurLimiteMontantVersement')
    };
  }

  @Selector([CapaciteState])
  static donneesEtapeHypotheque(state: CapaciteStateModel): EtapeHypothequeDataModel {
    return getFromPath(state, 'depensesResidenceCourante');
  }

  @Selector([CapaciteState])
  static donneesEtapePaiement(state: CapaciteStateModel): EtapePaiementDataModel {
    return {
      montantLimiteVersementMensuel: getFromPath(state, 'montantLimiteVersementMensuel')
    };
  }

  @Selector([CapaciteState])
  static donneesEtapeDepenses(state: CapaciteStateModel): EtapeDepensesDataModel {
    return {
      indicateurMontantTotalDepenses: getFromPath(state, 'indicateurMontantTotalDepenses'),
      montantTotalDepenses: getFromPath(state, 'montantTotalDepenses'),
      depensesMensuels: getFromPath(state, 'depensesMensuels')
    };
  }

  @Selector([
    CapaciteSelectors.donneesEtapeBudget,
    CapaciteSelectors.donneesEtapePaiement,
    CapaciteSelectors.donneesEtapeDepenses
  ])
  static donneesDepensesPaiement(
    budget: any,
    paiement: EtapePaiementDataModel,
    depenses: EtapeDepensesDataModel
  ): EtapeDepensesPaiementDataModel {
    return {
      ...budget,
      ...paiement,
      ...depenses
    };
  }

  @Selector([CapaciteSelectors.donneesCapacite, CapaciteSelectors.donneesEtapeHypotheque])
  static donneesPageResultat(capacite: CapaciteModel, donneesHypotheque: EtapeHypothequeDataModel): ResultatDataModel {
    const result: ResultatDataModel = {
      isTooLow: CapaciteHelper.isCapaciteBasse(capacite),
      isTooHigh: CapaciteHelper.isCapaciteHaute(capacite),
      maximale: {
        valeurPropriete: Math.ceil(capacite.valeurProprieteMaximale),
        versementMensuel: Math.ceil(capacite.versementMensuelMaximal),
        primeAssureurHypothecaire: Math.ceil(capacite.primeAssureurHypothecaireMaximale),
        montantPret: Math.ceil(capacite.montantPretMaximal),
        fraisProprieteMensuel: CapaciteHelper.getTotalFraisResidence(capacite.depensesEstimeesCapaciteMaximale)
      },
      realiste: {
        valeurPropriete: Math.ceil(capacite.valeurProprieteRealiste),
        versementMensuel: Math.ceil(capacite.versementMensuelRealiste),
        primeAssureurHypothecaire: Math.ceil(capacite.primeAssureurHypothecaireRealiste),
        montantPret: Math.ceil(capacite.montantPretRealiste),
        fraisProprieteMensuel: CapaciteHelper.getTotalFraisResidence(capacite.depensesEstimeesCapaciteRealiste)
      },
      revenusNet: {
        montant: capacite.bilanSommaire.revenuNet,
        frequence: FREQUENCES[capacite.bilanSommaire.codeFrequenceRevenuNet]
      },
      revenusBrut: {
        montant: capacite.bilanSommaire.revenuAnnuelBrut,
        frequence: FREQUENCES.ANNUEL
      },
      miseDeFonds: {
        montant: capacite.miseDeFonds
      },
      dettes: {
        montant: CapaciteHelper.getTotalEngagements(capacite, FrequenceEnum.MENSUEL),
        frequence: FREQUENCES.MENSUEL
      },
      hypotheque: null,
      paiement: null,
      depenses: null
    };

    if (capacite.typeCapacite === TypeCapaciteEnum.PME_SECONDAIRE) {
      result.hypotheque = {
        montant: CapaciteHelper.getTotalFraisResidence(donneesHypotheque),
        frequence: FREQUENCES.MENSUEL
      };
    }

    if (capacite.indicateurLimiteMontantVersement === 'O') {
      result.paiement = {
        montant: CapaciteHelper.getTotalBudget(capacite),
        frequence: FREQUENCES.MENSUEL
      };
    } else {
      result.depenses = {
        montant: CapaciteHelper.getTotalDepenses(capacite),
        frequence: FREQUENCES.MENSUEL
      };
    }

    return result;
  }

  @Selector([CapaciteState])
  static donneesResultat(state: CapaciteStateModel) {
    return {
      valeurProprieteMaximale: Math.ceil(state.capacite.valeurProprieteMaximale),
      versementMensuelMaximal: state.capacite.versementMensuelMaximal,
      primeAssureurHypothecaireMaximale: Math.ceil(state.capacite.primeAssureurHypothecaireMaximale),
      versementMensuelRealiste: state.capacite.versementMensuelRealiste,
      valeurProprieteRealiste: Math.ceil(state.capacite.valeurProprieteRealiste),
      primeAssureurHypothecaireRealiste: Math.ceil(state.capacite.primeAssureurHypothecaireRealiste),
      miseDeFonds: state.capacite.miseDeFonds,
      montantPretMaximal: Math.ceil(state.capacite.montantPretMaximal),
      montantPretRealiste: Math.ceil(state.capacite.montantPretRealiste),
      isTooLow: CapaciteHelper.isCapaciteBasse(state.capacite),
      isTooHigh: CapaciteHelper.isCapaciteHaute(state.capacite),
      isCompleted: state.completed,
      fraisProprieteMoyenne: state.fraisProprieteMoyenne
    };
  }

  @Selector([CapaciteState])
  static donneesResultatScenario(state: CapaciteStateModel) {
    const scenario = state.capaciteScenario;

    const valeurMaximale = scenario.valeurProprieteMaximale ? Math.ceil(scenario.valeurProprieteMaximale) : null;
    const valeurRealiste = scenario.valeurProprieteRealiste ? Math.ceil(scenario.valeurProprieteRealiste) : null;
    const versementMaximal = scenario.versementMensuelMaximal ? Math.ceil(scenario.versementMensuelMaximal) : null;
    const versementRealiste = scenario.versementMensuelRealiste ? Math.ceil(scenario.versementMensuelRealiste) : null;
    const isTooLow =
      scenario.montantPretMaximal !== null || scenario.montantPretRealiste !== null
        ? CapaciteHelper.isCapaciteBasse(scenario)
        : false;
    const isTooHigh =
      scenario.montantPretMaximal && scenario.montantPretRealiste ? CapaciteHelper.isCapaciteHaute(scenario) : false;

    return {
      valeurProprieteMaximale: valeurMaximale,
      versementMensuelMaximal: versementMaximal,
      versementMensuelRealiste: versementRealiste,
      valeurProprieteRealiste: valeurRealiste,
      isTooLow: isTooLow,
      isTooHigh: isTooHigh
    };
  }

  @Selector([CapaciteState])
  static donneesCapacite(state: CapaciteStateModel) {
    return state.capacite;
  }

  @Selector([CapaciteState])
  static totalDepenses(state: CapaciteStateModel) {
    return CapaciteHelper.getTotalDepenses(state.capacite);
  }

  @Selector([CapaciteState])
  static totalEngagements(state: CapaciteStateModel) {
    return CapaciteHelper.getTotalEngagements(state.capacite, FrequenceEnum.MENSUEL);
  }

  @Selector([CapaciteState])
  static revenuAnnuelBrut(state: CapaciteStateModel) {
    return getFromPath(state, 'bilanSommaire.revenuAnnuelBrut');
  }

  @Selector([CapaciteState])
  static isCapacityFormInProgress(state: CapaciteStateModel): boolean {
    return state.currentStep !== null;
  }

  @Selector([CapaciteState])
  static isCapacityModifying(state: CapaciteStateModel): boolean {
    return state.modifyingStep !== null;
  }

  @Selector([CapaciteState])
  static modifyingStep(state: CapaciteStateModel): CapaciteEditStepEnum {
    return state.modifyingStep;
  }

  @Selector([CapaciteState])
  static isCapacityStarted(state: CapaciteStateModel): boolean {
    // Temporaire en attendant de pouvoir retourner directement à la dernière étape
    return state.capacite.typePropriete !== null;
  }

  @Selector([CapaciteState])
  static isResidenceSecondaire(state: CapaciteStateModel): boolean {
    return state.capacite.typeCapacite === TypeCapaciteEnum.PME_SECONDAIRE;
  }

  @Selector([CapaciteState, CapaciteSelectors.isCapacityStarted, CapaciteSelectors.isCapacityCompleted])
  static statutCapacite(
    state: CapaciteStateModel,
    isCapacityStarted: boolean,
    isCapacityCompleted: boolean
  ): StatusCapaciteEnum {
    if (isCapacityStarted && !isCapacityCompleted) {
      return StatusCapaciteEnum.Debute;
    }
    if (isCapacityCompleted) {
      if (CapaciteHelper.isCapaciteBasse(state.capacite)) {
        return StatusCapaciteEnum.Basse;
      }

      if (CapaciteHelper.isCapaciteHaute(state.capacite)) {
        return StatusCapaciteEnum.Haute;
      }

      if (CapaciteHelper.isEqualCapacity(state.capacite)) {
        return StatusCapaciteEnum.RealisteEgaleMaximale;
      }

      return StatusCapaciteEnum.RealisteMaximale;
    } else {
      return StatusCapaciteEnum.Vide;
    }
  }
}
